import axios, { AxiosError } from 'axios';

/**
 * Requirement Service
 *
 * Handles all requirement-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

/**
 * Requirement interfaces
 */
export interface Requirement {
  id: string;
  project_id: string;
  title: string;
  description?: string | null;
  acceptance_criteria?: string | null;
  requirement_type: 'functional' | 'non-functional' | 'technical' | 'business' | 'user-story';
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'draft' | 'approved' | 'in-progress' | 'completed' | 'rejected' | 'deferred';
  approval_status: 'pending' | 'approved' | 'rejected';
  hierarchy_type: 'parent' | 'child';
  parent_requirement_id?: string | null;
  created_by: string;
  assigned_to?: string | null;
  created_at: string;
  updated_at?: string | null;
  is_active: boolean;
  // AI-Generated Insights fields
  insights?: string | null;
  insights_generated_at?: string | null;
  insights_tokens_used?: number | null;
  insights_model?: string | null;
  // JIRA Integration fields
  jira_status?: string | null;
  // Scheduling & Timeline fields
  expected_start_date?: string | null;
  expected_duration_days?: number | null;
  actual_start_date?: string | null;
  actual_end_date?: string | null;
  actual_duration_days?: number | null;
  schedule_variance_days?: number | null;
  due_date?: string | null;
  is_on_schedule: boolean;
  is_overdue: boolean;
  requirement_code?: string | null;
}

export interface RequirementWithDetails extends Requirement {
  created_by_name: string;
  created_by_email: string;
  assigned_to_name?: string | null;
  assigned_to_email?: string | null;
  comment_count?: number;
  reply_count?: number;
  latest_comment_date?: string | null;
  child_count?: number;
  dependencies?: string | null; // Comma-separated list of requirement IDs that this requirement depends on (for Gantt chart)
}

export interface CreateRequirementInput {
  title: string;
  description?: string;
  acceptance_criteria?: string;
  requirement_type?: 'functional' | 'non-functional' | 'technical' | 'business' | 'user-story';
  priority?: 'critical' | 'high' | 'medium' | 'low';
  approval_status?: 'pending' | 'approved' | 'rejected';
  hierarchy_type?: 'parent' | 'child';
  parent_requirement_id?: string;
  assigned_to?: string;
  // Scheduling & Timeline fields
  expected_start_date?: string | null;
  due_date?: string | null;
}

export interface UpdateRequirementInput {
  title?: string;
  description?: string;
  acceptance_criteria?: string;
  requirement_type?: 'functional' | 'non-functional' | 'technical' | 'business' | 'user-story';
  priority?: 'critical' | 'high' | 'medium' | 'low';
  status?: 'draft' | 'approved' | 'in-progress' | 'completed' | 'rejected' | 'deferred';
  approval_status?: 'pending' | 'approved' | 'rejected';
  assigned_to?: string;
  // Scheduling & Timeline fields
  expected_start_date?: string | null;
  due_date?: string | null;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Get all requirements for a project
 */
export async function getProjectRequirements(
  projectId: string,
  token: string
): Promise<RequirementWithDetails[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/requirements`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get all parent requirements for a project (for dropdown when creating child requirements)
 */
export async function getParentRequirements(
  projectId: string,
  token: string
): Promise<RequirementWithDetails[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/requirements/parents`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get a single requirement by ID
 */
export async function getRequirementById(
  projectId: string,
  requirementId: string,
  token: string
): Promise<RequirementWithDetails> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Create a new requirement
 */
export async function createRequirement(
  projectId: string,
  data: CreateRequirementInput,
  token: string
): Promise<Requirement> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update a requirement
 */
export async function updateRequirement(
  projectId: string,
  requirementId: string,
  data: UpdateRequirementInput,
  token: string
): Promise<Requirement> {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Delete a requirement
 */
export async function deleteRequirement(
  projectId: string,
  requirementId: string,
  token: string
): Promise<void> {
  try {
    await axios.delete(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Approve a requirement
 */
export async function approveRequirement(
  projectId: string,
  requirementId: string,
  token: string
): Promise<Requirement> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/approve`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Reject a requirement
 */
export async function rejectRequirement(
  projectId: string,
  requirementId: string,
  token: string
): Promise<Requirement> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/reject`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Bulk approve multiple requirements
 */
export async function bulkApproveRequirements(
  projectId: string,
  requirementIds: string[],
  token: string
): Promise<{ count: number }> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements/bulk-approve`,
      { requirement_ids: requirementIds },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update requirement display order
 */
export async function updateRequirementOrder(
  projectId: string,
  requirementId: string,
  newOrder: number,
  token: string
): Promise<void> {
  try {
    await axios.put(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/order`,
      { new_order: newOrder },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Bulk import AI-generated requirements
 *
 * @param projectId - Project ID
 * @param requirements - Array of requirements to import
 * @param token - Authentication token
 * @returns Response with created requirements, count, and remaining allocation
 */
export async function importAIRequirements(
  projectId: string,
  requirements: CreateRequirementInput[],
  token: string
): Promise<{
  success: boolean;
  message: string;
  data: {
    requirements: Requirement[];
    ai_generated_count: number;
    remaining: number;
  };
}> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements/import-ai`,
      { requirements },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error: any) {
    handleAPIError(error);
  }
}

/**
 * Generate acceptance criteria using AI
 */
export async function generateAcceptanceCriteriaAI(
  projectId: string,
  data: { title: string; description: string; requirement_id?: string },
  token: string
): Promise<{ acceptance_criteria: string; criteria_items: string[]; criteria_records?: any[]; tokens_used: number; model: string }> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements/generate-acceptance-criteria`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

// ─── Acceptance Criteria ────────────────────────────────────────

export async function getAcceptanceCriteria(projectId: string, requirementId: string, token: string) {
  const response = await axios.get(
    `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/acceptance-criteria`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function createAcceptanceCriterion(
  projectId: string,
  requirementId: string,
  data: { title: string },
  token: string
) {
  const response = await axios.post(
    `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/acceptance-criteria`,
    { requirement_id: requirementId, ...data },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function bulkCreateAcceptanceCriteria(
  projectId: string,
  requirementId: string,
  items: { title: string }[],
  token: string
) {
  const response = await axios.post(
    `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/acceptance-criteria/bulk`,
    { items },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function toggleAcceptanceCriterion(projectId: string, requirementId: string, criterionId: string, token: string) {
  const response = await axios.put(
    `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/acceptance-criteria/${criterionId}/toggle`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function deleteAcceptanceCriterion(projectId: string, requirementId: string, criterionId: string, token: string) {
  const response = await axios.delete(
    `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/acceptance-criteria/${criterionId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export default {
  getProjectRequirements,
  getRequirementById,
  createRequirement,
  updateRequirement,
  deleteRequirement,
  approveRequirement,
  rejectRequirement,
  bulkApproveRequirements,
  updateRequirementOrder,
  importAIRequirements,
  generateAcceptanceCriteriaAI,
  getAcceptanceCriteria,
  createAcceptanceCriterion,
  bulkCreateAcceptanceCriteria,
  toggleAcceptanceCriterion,
  deleteAcceptanceCriterion
};
